import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Search, Plus, ShoppingBag, Sparkles, Tag, MapPin, User, ImagePlus, X, Bookmark, Flag, Star, RefreshCw, MessageCircle, Calendar, Store, Share2 } from "lucide-react";
import { getListings, createListing, generateDescription, uploadImage, resolveImageUrl, toggleBookmark, reportContent, getAllFeaturedListings, toggleFeaturedListing, getWeeklySpotlight, getListingsV2, getMyShop, createListingV2 } from "@/lib/api";
import { ShareDialog } from "@/components/ShareButton";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AdvancedFilters, ActiveFilters } from "@/components/AdvancedFilters";
import RichTextEditor, { isContentEmpty } from "@/components/RichTextEditor";
import { MembershipBanner } from "@/components/MembershipModal";
import { useSEO } from "@/hooks/useSEO";

const CATEGORIES = ["Rosaries", "Bibles & Books", "Icons & Art", "Candles & Devotionals", "Handmade Crafts", "Clothing & Vestments", "Home & Garden", "Other"];

function CreateListingDialog({ user, onCreated, userShop }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("");
  const [condition, setCondition] = useState("new");
  const [mediaFiles, setMediaFiles] = useState([]); // Multiple files
  const [mediaPreviews, setMediaPreviews] = useState([]); // Preview URLs
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  // Location and additional info
  const [location, setLocation] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("us");
  const [customFaq, setCustomFaq] = useState("");

  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Limit to 10 files
    const totalFiles = mediaFiles.length + files.length;
    if (totalFiles > 10) {
      toast.error("Maximum 10 images/videos allowed");
      return;
    }
    
    const validFiles = [];
    const newPreviews = [];
    
    for (const file of files) {
      // Check file size (10MB for images, 1GB for videos)
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? 1024 * 1024 * 1024 : 10 * 1024 * 1024;
      
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max ${isVideo ? '1GB' : '10MB'}`);
        continue;
      }
      
      validFiles.push(file);
      newPreviews.push({
        url: URL.createObjectURL(file),
        type: isVideo ? 'video' : 'image',
        name: file.name
      });
    }
    
    setMediaFiles([...mediaFiles, ...validFiles]);
    setMediaPreviews([...mediaPreviews, ...newPreviews]);
  };

  const removeMedia = (index) => {
    const newFiles = [...mediaFiles];
    const newPreviews = [...mediaPreviews];
    URL.revokeObjectURL(newPreviews[index].url);
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setMediaFiles(newFiles);
    setMediaPreviews(newPreviews);
  };

  const handleAiDescription = async () => {
    if (!title || !category) {
      toast.error("Enter a title and category first");
      return;
    }
    setAiLoading(true);
    try {
      const res = await generateDescription({ title, category });
      setDescription(res.data.description);
      toast.success("AI description generated!");
    } catch {
      toast.error("Failed to generate description");
    }
    setAiLoading(false);
  };

  const handleSubmit = async () => {
    // Check if user has a shop
    if (!userShop) {
      toast.error("You must create a shop before listing items for sale. Go to 'My Shop' to set up your shop first.");
      return;
    }
    
    if (!title || isContentEmpty(description) || !price || !category) {
      toast.error("Fill in all required fields");
      return;
    }
    setLoading(true);
    
    // Upload all media files
    const uploadedUrls = [];
    for (const file of mediaFiles) {
      try {
        const uploadRes = await uploadImage(file);
        uploadedUrls.push(uploadRes.data.url);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    try {
      await createListingV2({
        title, description, price: parseFloat(price), category, condition,
        image_urls: uploadedUrls,
        listing_type: "item",
        location,
        zip_code: zipCode || null,
        country: country || "us",
        custom_faq: customFaq || null,
        shop_id: userShop.shop_id
      });
      toast.success("Item listed for sale!");
      setOpen(false);
      // Reset form
      setTitle(""); setDescription(""); setPrice(""); setCategory(""); 
      setMediaFiles([]); setMediaPreviews([]); setLocation("");
      setZipCode(""); setCountry("us"); setCustomFaq("");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create listing");
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold" data-testid="create-listing-btn">
          <Plus className="w-4 h-4 mr-2" /> Sell Item
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26]">List Item for Sale</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Shop Requirement Notice */}
          {userShop ? (
            <div className="p-3 bg-[#F9F7F2] rounded-lg border border-[#E5E0D5]">
              <div className="flex items-center gap-3">
                <Store className="w-5 h-5 text-[var(--liturgical-primary)]" />
                <div>
                  <p className="text-xs text-[#6B665E] font-['Nunito']">Selling from your shop</p>
                  <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">{userShop.name}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-3">
                <Store className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 font-['Nunito']">Shop Required</p>
                  <p className="text-xs text-amber-700 font-['Nunito'] mt-1">You must create a shop before listing items for sale.</p>
                  <button 
                    onClick={() => { setOpen(false); navigate("/my-shop"); }}
                    className="mt-2 px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-['Nunito'] font-semibold hover:bg-amber-700"
                  >
                    Create Your Shop
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Category */}
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']" data-testid="listing-category-select">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D5]">
                {CATEGORIES.map(c => <SelectItem key={c} value={c} className="font-['Nunito'] text-sm">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          {/* Title */}
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Handcrafted Olive Wood Rosary" className="border-[#E5E0D5] font-['Nunito'] text-sm focus-visible:ring-[var(--liturgical-primary)]" data-testid="listing-title-input" />
          </div>
          
          {/* Description */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Description *</Label>
              <Button variant="ghost" size="sm" onClick={handleAiDescription} disabled={aiLoading} className="text-[var(--liturgical-secondary)] hover:text-[#C4A02F] hover:bg-[var(--liturgical-secondary)]/10 text-xs font-['Nunito'] h-7" data-testid="ai-description-btn">
                <Sparkles className="w-3 h-3 mr-1" /> {aiLoading ? "Generating..." : "AI Write"}
              </Button>
            </div>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Describe your item or service..."
              minHeight="100px"
            />
          </div>
          
          {/* Price & Condition */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Price ($) *</Label>
              <Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0.00" className="border-[#E5E0D5] font-['Nunito'] text-sm focus-visible:ring-[var(--liturgical-primary)]" data-testid="listing-price-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Condition</Label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger className="border-[#E5E0D5] text-sm font-['Nunito']" data-testid="listing-condition-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E5E0D5]">
                  <SelectItem value="new" className="font-['Nunito'] text-sm">New</SelectItem>
                  <SelectItem value="like_new" className="font-['Nunito'] text-sm">Like New</SelectItem>
                  <SelectItem value="good" className="font-['Nunito'] text-sm">Good</SelectItem>
                  <SelectItem value="fair" className="font-['Nunito'] text-sm">Fair</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Additional Info / FAQ */}
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Additional Info (Optional)</Label>
            <RichTextEditor
              value={customFaq}
              onChange={setCustomFaq}
              placeholder="Add any additional information buyers might want to know..."
              minHeight="80px"
              minimal={true}
            />
          </div>
          
          {/* Media Upload (Multiple Images/Videos) */}
          <div className="space-y-1.5">
            <Label className="font-['Nunito'] text-xs text-[#2D2A26]">Photos & Videos (up to 10)</Label>
            <input type="file" ref={fileInputRef} accept="image/*,video/*" multiple onChange={handleMediaSelect} className="hidden" />
            
            {mediaPreviews.length > 0 ? (
              <div className="space-y-2">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {mediaPreviews.map((preview, idx) => (
                    <div key={idx} className="relative aspect-square">
                      {preview.type === 'video' ? (
                        <div className="w-full h-full bg-gray-100 rounded-md border border-[#E5E0D5] flex items-center justify-center">
                          <span className="text-xs text-gray-500">Video</span>
                        </div>
                      ) : (
                        <img src={preview.url} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover rounded-md border border-[#E5E0D5]" />
                      )}
                      <button 
                        onClick={() => removeMedia(idx)} 
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                        data-testid={`remove-media-${idx}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {mediaPreviews.length < 10 && (
                    <button 
                      onClick={() => fileInputRef.current?.click()} 
                      className="aspect-square border-2 border-dashed border-[#E5E0D5] rounded-md flex flex-col items-center justify-center text-[#6B665E] hover:border-[var(--liturgical-primary)] hover:text-[var(--liturgical-primary)] transition-colors"
                      data-testid="add-more-media-btn"
                    >
                      <ImagePlus className="w-5 h-5" />
                      <span className="text-xs mt-1">Add</span>
                    </button>
                  )}
                </div>
                <p className="text-xs text-[#6B665E]">{mediaPreviews.length}/10 files</p>
              </div>
            ) : (
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full border-dashed border-[#E5E0D5] text-[#6B665E] font-['Nunito'] text-xs h-20 flex flex-col gap-1" data-testid="listing-upload-media-btn">
                <ImagePlus className="w-5 h-5" />
                Upload Photos & Videos
              </Button>
            )}
          </div>
          
          {/* Submit Button */}
          <Button onClick={handleSubmit} disabled={loading || !userShop} className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold h-10 active:scale-95 transition-all disabled:opacity-50" data-testid="listing-submit-btn">
            {loading ? "Creating..." : "List Item for Sale"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ListingCard({ listing, currentUserId, isAdmin, onFeatureToggle }) {
  const navigate = useNavigate();
  const [bookmarked, setBookmarked] = useState(false);
  const [featuring, setFeaturing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const handleCardClick = () => {
    navigate(`/listing/${listing.listing_id}`);
  };

  const isOwnListing = listing.seller?.user_id === currentUserId;

  const handleBookmark = async (e) => {
    e.stopPropagation();
    try {
      const res = await toggleBookmark({ content_type: "listing", content_id: listing.listing_id });
      setBookmarked(res.data.bookmarked);
      
      if (res.data.bookmarked && !isOwnListing) {
        // Show enhanced toast with "Message Seller" option
        toast.success(
          <div className="flex flex-col gap-2">
            <span className="font-semibold">Saved to bookmarks!</span>
            <span className="text-sm text-gray-600">Interested? Contact the seller directly.</span>
            <button 
              onClick={(btnE) => {
                btnE.stopPropagation();
                navigate(`/messages?to=${listing.seller?.user_id}`);
              }}
              className="mt-1 px-3 py-1.5 bg-[var(--liturgical-primary)] text-white rounded-md text-sm font-medium hover:brightness-90 transition-all"
            >
              Message Seller
            </button>
          </div>,
          { duration: 5000 }
        );
      } else if (!res.data.bookmarked) {
        toast.success("Removed from bookmarks");
      }
    } catch {
      toast.error("Failed to bookmark");
    }
  };

  const handleReport = async (e) => {
    e.stopPropagation();
    if (window.confirm("Report this listing as inappropriate?")) {
      try {
        await reportContent({ content_type: "listing", content_id: listing.listing_id, reason: "inappropriate" });
        toast.success("Report submitted");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to report");
      }
    }
  };

  const handleFeature = async (e) => {
    e.stopPropagation();
    setFeaturing(true);
    try {
      const res = await toggleFeaturedListing(listing.listing_id);
      toast.success(res.data.featured ? "Listing featured!" : "Listing unfeatured");
      if (onFeatureToggle) onFeatureToggle();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to feature listing");
    }
    setFeaturing(false);
  };

  const isSold = listing.status === "sold";
  const isFeatured = listing.featured;
  const canFeature = isAdmin && listing.parish_id && !isSold;

  return (
    <Card className={`bg-white shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group h-full flex flex-col ${isSold ? 'opacity-75' : ''} ${isFeatured ? 'border-2 border-[var(--liturgical-secondary)] ring-2 ring-[#D4AF37]/20' : 'border border-[#E5E0D5]'}`} onClick={handleCardClick} data-testid={`listing-card-${listing.listing_id}`}>
      {/* Image container with 4:5 aspect ratio (25% taller than wide) */}
      <div className="relative w-full" style={{ paddingBottom: '125%' }}>
        {listing.image_url ? (
          <img 
            src={resolveImageUrl(listing.image_url)} 
            alt={listing.title} 
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-[#F0EBE0] flex items-center justify-center">
            <ShoppingBag className="w-12 h-12 text-[#E5E0D5]" />
          </div>
        )}
        {isSold && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-[var(--liturgical-primary)] text-white px-4 py-1 rounded-full font-['Nunito'] font-semibold text-sm">SOLD</span>
          </div>
        )}
        {isFeatured && !isSold && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-[var(--liturgical-secondary)] text-[#2D2A26] text-[10px] font-['Nunito'] font-semibold">
              <Star className="w-3 h-3 mr-1 fill-current" /> Featured
            </Badge>
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-1">
          {canFeature && (
            <button
              onClick={handleFeature}
              disabled={featuring}
              className={`p-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm transition-colors ${isFeatured ? 'text-[var(--liturgical-secondary)]' : 'text-[#6B665E] hover:text-[var(--liturgical-secondary)]'}`}
              title={isFeatured ? "Remove from featured" : "Feature this listing"}
              data-testid={`listing-feature-${listing.listing_id}`}
            >
              <Star className={`w-4 h-4 ${isFeatured ? 'fill-current' : ''}`} />
            </button>
          )}
          <button
            onClick={handleBookmark}
            className={`p-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm transition-colors ${bookmarked ? 'text-[var(--liturgical-secondary)]' : 'text-[#6B665E] hover:text-[var(--liturgical-secondary)]'}`}
            data-testid={`listing-bookmark-${listing.listing_id}`}
          >
            <Bookmark className={`w-4 h-4 ${bookmarked ? 'fill-current' : ''}`} />
          </button>
          <button
            onClick={handleReport}
            className="p-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm text-[#6B665E] hover:text-[#991B1B] transition-colors"
            data-testid={`listing-report-${listing.listing_id}`}
          >
            <Flag className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setShareOpen(true); }}
            className="p-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm text-[#6B665E] hover:text-[var(--liturgical-primary)] transition-colors"
            title="Share"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Content section with fixed height */}
      <CardContent className="p-3 flex flex-col flex-1">
        <h3 className="font-['Crimson_Text'] text-base font-semibold text-[#2D2A26] leading-tight line-clamp-2 mb-1">{listing.title}</h3>
        <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] text-lg mb-1">${listing.price}</span>
        {listing.average_rating > 0 && (
          <div className="flex items-center gap-1 mb-1" data-testid={`listing-rating-${listing.listing_id}`}>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className={`w-3 h-3 ${s <= Math.round(listing.average_rating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#E5E0D5]'}`} />
              ))}
            </div>
            <span className="text-[10px] text-[#6B665E] font-['Nunito']">({listing.review_count})</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[9px] font-['Nunito'] px-1.5 py-0">
            <Tag className="w-2 h-2 mr-0.5" />{listing.category}
          </Badge>
        </div>
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-[#F0EBE0]">
          {listing.shop ? (
            <button 
              onClick={(e) => { e.stopPropagation(); navigate(`/shop/${listing.shop.shop_id}`); }}
              className="flex items-center gap-1.5 min-w-0 flex-1 hover:text-[var(--liturgical-primary)] transition-colors"
              data-testid={`listing-shop-link-${listing.listing_id}`}
            >
              <Store className="w-3 h-3 text-[var(--liturgical-primary)] flex-shrink-0" />
              <span className="text-[10px] text-[var(--liturgical-primary)] font-['Nunito'] font-semibold truncate">{listing.shop.name}</span>
            </button>
          ) : listing.seller && (
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <User className="w-3 h-3 text-[#6B665E] flex-shrink-0" />
              <span className="text-[10px] text-[#6B665E] font-['Nunito'] truncate">{listing.seller.name}</span>
            </div>
          )}
          {listing.distance_formatted ? (
            <div className="flex items-center gap-1 text-[10px] text-[var(--liturgical-primary)] font-['Nunito'] font-semibold">
              <MapPin className="w-3 h-3" />
              <span>{listing.distance_formatted}</span>
            </div>
          ) : listing.location && (
            <div className="flex items-center gap-1 text-[10px] text-[#6B665E] font-['Nunito']">
              <MapPin className="w-3 h-3" />
              <span className="truncate max-w-[80px]">{listing.location}</span>
            </div>
          )}
        </div>
      </CardContent>
      {shareOpen && (
        <ShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          title={listing.title}
          description={`$${listing.price} · ${listing.category}`}
          url={`/listing/${listing.listing_id}`}
          type="listing"
        />
      )}
    </Card>
  );
}

export default function MarketplacePage({ user, isPublic = false }) {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [spotlightData, setSpotlightData] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [listingType, setListingType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [userShop, setUserShop] = useState(null);
  const [filters, setFilters] = useState({
    category: "all",
    condition: "all",
    minPrice: "",
    maxPrice: "",
    parishId: "all",
    sortBy: "newest",
    listingType: "all",
    userZip: "",
    userCountry: "us",
    maxDistance: "any"
  });

  // SEO
  useSEO({
    title: "Marketplace",
    description: "Buy and sell religious items, handmade crafts, and services within the Catholic community. Find rosaries, icons, books, and more from fellow parishioners.",
    path: "/marketplace"
  });

  const isAdmin = user?.role === "parish_admin" && !isPublic;

  const loadUserShop = async () => {
    try {
      const res = await getMyShop();
      setUserShop(res.data.shop);
    } catch {}
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.listingType !== "all") params.listing_type = filters.listingType;
      if (filters.condition !== "all") params.condition = filters.condition;
      if (filters.minPrice) params.min_price = parseFloat(filters.minPrice);
      if (filters.maxPrice) params.max_price = parseFloat(filters.maxPrice);
      if (filters.parishId !== "all") params.parish_id = filters.parishId;
      if (filters.sortBy) params.sort_by = filters.sortBy;
      
      // Distance filtering params
      if (filters.userZip && filters.maxDistance && filters.maxDistance !== "any") {
        params.user_zip = filters.userZip;
        params.user_country = filters.userCountry || "us";
        params.max_distance = parseFloat(filters.maxDistance);
      }
      
      const res = await getListingsV2(params);
      setListings(res.data);
    } catch {}
    setLoading(false);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRemoveFilter = (key) => {
    if (key === "all") {
      setFilters({
        category: "all",
        condition: "all",
        minPrice: "",
        maxPrice: "",
        parishId: "all",
        sortBy: "newest",
        listingType: "all",
        userZip: "",
        userCountry: "us",
        maxDistance: "any"
      });
    } else if (key === "distance") {
      setFilters({ ...filters, maxDistance: "any", userZip: "" });
    } else {
      const defaultValues = { category: "all", condition: "all", minPrice: "", maxPrice: "", parishId: "all", sortBy: "newest", listingType: "all", userZip: "", userCountry: "us", maxDistance: "any" };
      setFilters({ ...filters, [key]: defaultValues[key] });
    }
  };

  const loadFeaturedListings = async () => {
    try {
      const res = await getAllFeaturedListings();
      setFeaturedListings(res.data);
    } catch {}
  };

  const loadSpotlight = async () => {
    try {
      const res = await getWeeklySpotlight();
      setSpotlightData(res.data);
    } catch {}
  };

  // Reload listings when filters change
  useEffect(() => {
    loadListings();
  }, [filters, search]);

  // Load featured, spotlight and user shop on mount
  useEffect(() => {
    loadFeaturedListings();
    loadSpotlight();
    loadUserShop();
  }, []);

  const handleFeatureToggle = () => {
    loadListings();
    loadFeaturedListings();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadListings();
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="marketplace-page">
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Public Access Banner */}
        {isPublic && (
          <MembershipBanner context="marketplace" className="mb-6" />
        )}
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Crimson_Text'] text-4xl font-bold text-[#2D2A26] tracking-tight" data-testid="marketplace-title">Marketplace</h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">Religious items and handmade crafts from the faithful</p>
          </div>
          {!isPublic && (
            <div className="flex items-center gap-3">
              {userShop && (
                <Button
                  variant="outline"
                  onClick={() => navigate("/my-shop")}
                  className="border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] font-['Nunito']"
                  data-testid="my-shop-btn"
                >
                  <Store className="w-4 h-4 mr-2" /> My Shop
                </Button>
              )}
              <CreateListingDialog user={user} onCreated={loadListings} userShop={userShop} />
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#6B665E]" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search listings..."
                className="pl-10 border-[#E5E0D5] font-['Nunito'] text-sm focus-visible:ring-[var(--liturgical-primary)]"
                data-testid="marketplace-search-input"
              />
            </div>
            <Button type="submit" variant="outline" className="border-[#E5E0D5] text-[#2D2A26] font-['Nunito'] text-sm" data-testid="marketplace-search-btn">Search</Button>
          </form>
          <AdvancedFilters 
            filters={filters} 
            onFiltersChange={handleFiltersChange}
            categories={CATEGORIES}
          />
        </div>

        {/* Active Filters */}
        <ActiveFilters 
          filters={filters}
          onRemoveFilter={handleRemoveFilter}
          categories={CATEGORIES}
        />

        {/* Weekly Spotlight Section */}
        {spotlightData && spotlightData.items && spotlightData.items.length > 0 && (
          <div className="mb-10 bg-gradient-to-r from-[#556B2F]/5 to-[#3E7B40]/5 rounded-xl p-6 border border-[var(--liturgical-primary)]/20" data-testid="weekly-spotlight-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-[var(--liturgical-primary)]" />
                </div>
                <div>
                  <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26]">Weekly Spotlight</h2>
                  <p className="text-xs text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Week {spotlightData.week_number} • Rotates every Monday
                  </p>
                </div>
              </div>
              <Badge className="bg-[var(--liturgical-primary)] text-white font-['Nunito'] text-xs">
                {spotlightData.items.length} items from {new Set(spotlightData.items.map(i => i.parish_name)).size} parishes
              </Badge>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {spotlightData.items.slice(0, 8).map(listing => (
                <Card 
                  key={listing.listing_id} 
                  className="bg-white border-[var(--liturgical-primary)]/30 shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden h-full flex flex-col"
                  onClick={() => navigate(`/listing/${listing.listing_id}`)}
                  data-testid={`spotlight-card-${listing.listing_id}`}
                >
                  {/* Image with 4:5 aspect ratio */}
                  <div className="relative w-full" style={{ paddingBottom: '125%' }}>
                    {listing.image_url ? (
                      <img src={resolveImageUrl(listing.image_url)} alt={listing.title} className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 w-full h-full bg-[#F0EBE0] flex items-center justify-center">
                        <ShoppingBag className="w-8 h-8 text-[#E5E0D5]" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3 flex flex-col flex-1">
                    <h3 className="font-['Crimson_Text'] text-sm font-semibold text-[#2D2A26] line-clamp-2 mb-1">{listing.title}</h3>
                    <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] text-base mb-2">${listing.price}</span>
                    <div className="flex items-center justify-between mt-auto">
                      <Badge variant="outline" className="border-[var(--liturgical-primary)]/30 text-[var(--liturgical-primary)] text-[9px] font-['Nunito'] truncate max-w-[60%]">
                        {listing.parish_name}
                      </Badge>
                      <span className="text-[9px] text-[#6B665E] font-['Nunito'] truncate">{listing.seller?.name}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* All Items Grid */}
        <div>
          <h2 className="font-['Crimson_Text'] text-xl font-semibold text-[#2D2A26] mb-4">All Items</h2>
          {loading ? (
            <div className="text-center py-16 text-[#6B665E] font-['Nunito']">Loading listings...</div>
          ) : listings.length === 0 ? (
            <div className="text-center py-16" data-testid="empty-marketplace">
              <ShoppingBag className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
              <p className="text-[#6B665E] font-['Nunito']">No items found. Create the first one!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {listings.map(listing => (
                <ListingCard 
                  key={listing.listing_id} 
                  listing={listing} 
                  currentUserId={user?.user_id}
                  isAdmin={isAdmin}
                  onFeatureToggle={handleFeatureToggle}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
